import dayjs from "dayjs";
import { LoaderFunction, redirect, useLoaderData, useNavigate } from "react-router-dom";

import { useTranslation } from "@/core/i18n";

import { getRepeatCashTransactions, getUser } from "api/methods";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { formatCurrency } from "toolbox/Money";
import { waitObj } from "toolbox/Promise";

const load = async (transactionId: string) => {
  const repeatCashTransactions = queryClient.fetchQuery({ ...getRepeatCashTransactions.keyed() });
  const user = queryClient.fetchQuery({ ...getUser.keyed() });
  const data = await waitObj({
    repeatCashTransactions,
    user,
  });
  const transactions = data.repeatCashTransactions?.transactions ?? [];
  const transaction = transactions.find((i) => i.id === transactionId);

  return { transaction, user: data.user };
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const repeatCashActivityDetailsLoader: LoaderFunction = async ({
  params: { transactionId },
}) => {
  if (!transactionId) return redirect("..");
  return await load(transactionId);
};

export const RepeatCashActivityDetailsPage = () => {
  const { t } = useTranslation();
  const { transaction, user } = useLoaderData() as LoaderData;
  const isAdded = transaction?.transactionType == "CashAdded";
  const navigate = useNavigate();

  const headerLabel = isAdded
    ? t("repeatCashActivityDetailsPage.additionDetailsHeader")
    : t("repeatCashActivityDetailsPage.deductionDetailsHeader");
  const reasonHeader = isAdded
    ? t("repeatCashActivityDetailsPage.additionReasonHeader")
    : t("repeatCashActivityDetailsPage.deductionReasonHeader");
  const amountLabel = isAdded
    ? t("repeatCashActivityDetailsPage.repeatCashAddedLabel")
    : t("repeatCashActivityDetailsPage.repeatCashDeductedLabel");

  return (
    <div className="relative h-full bg-one">
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => navigate(-1)}
      >
        <div className="flex justify-center text-sub2">{headerLabel}</div>
      </TopNav>
      <div className="p-5">
        <div className="flex flex-col gap-5 rounded-lg bg-white px-7 py-6 shadow-subtle">
          <div className="flex flex-col gap-2 border-b-[1px] border-b-light-grey pb-5">
            <div className="mb-2 flex items-center gap-1 text-body2">
              <span className="text-bold1">
                {t("repeatCashActivityDetailsPage.transactionLabel")}
              </span>
              {transaction?.id.split("-").pop()}
            </div>
            <div className="flex items-center gap-1 text-body2">
              <span className="text-bold2">{t("repeatCashActivityDetailsPage.dateLabel")} </span>
              {dayjs(transaction?.created).format("MM/DD/YY")}
            </div>
            <div className="flex items-center gap-1 text-body2">
              <span className="text-bold2">
                {t("repeatCashActivityDetailsPage.locationLabel")}{" "}
              </span>
              {transaction?.clientLocation || user.platformLocation?.name}
            </div>
            <div className="flex items-center gap-1 text-body2">
              <span className="text-bold2">{amountLabel} </span>
              {transaction?.transactionType === "CashAdded" ? "" : "-"}
              {formatCurrency(transaction?.amountInCents ?? 0)}
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <div className="text-bold3 uppercase">{reasonHeader}</div>
            <div className="text-body2">
              {transaction?.reason || transaction?.activityDescription}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
